import { AfterContentInit, Component, DestroyRef, Output, EventEmitter, Optional, Self, OnInit, Input, inject, signal } from '@angular/core';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NgControl, FormControl } from '@angular/forms';
import { Observable, timer, takeWhile, map, startWith, distinctUntilChanged } from 'rxjs';
import { OtpInputComponent } from '@app/shared/ui/otp/otp.component';
import { SharedModule } from '@app/shared/shared.module';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { INVALID_CODE_ERROR, VERIFICATION_CODE_LENGTH } from './code-verification.utils';

type CodeCheckState = 'verifying' | 'valid' | 'invalid';

@Component({
  selector: 'c-code-verification',
  templateUrl: './code-verification.component.html',
  styleUrl: './code-verification.component.scss',
  imports: [OtpInputComponent, SharedModule, TranslocoPipe, ButtonComponent],
  host: {
    '[class.code-verification--invalid]': "checkState() === 'invalid'",
  },
})
export class CodeVerificationComponent implements ControlValueAccessor, OnInit, AfterContentInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() otpLength = VERIFICATION_CODE_LENGTH;
  // Optional per-usage override of the OTP box styling, forwarded to c-otp-input.
  @Output() sendVerificationCode = new EventEmitter();

  countdown$: Observable<number>;

  readonly validIcon = faCircleCheck;
  readonly invalidIcon = faCircleXmark;

  /** Outcome of the server check; only set when the control has `verificationCodeValidator`. */
  readonly checkState = signal<CodeCheckState | null>(null);

  onChange: any = () =>  {};
  onTouch: any = () => {};

  get control() {
    return this.ngControl?.control as FormControl;
  }

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.startCountdown();
  }

  ngAfterContentInit(): void {
    const control = this.control;
    if (!control) {
      return;
    }

    control.statusChanges
      .pipe(
        startWith(control.status),
        map(() => this.toCheckState(control)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((state) => this.checkState.set(state));
  }

  private toCheckState(control: FormControl): CodeCheckState | null {
    // Without a server check, a complete code says nothing about validity.
    if (!control.asyncValidator) {
      return null;
    }

    if (control.pending) {
      return 'verifying';
    }

    if (control.hasError(INVALID_CODE_ERROR)) {
      return 'invalid';
    }

    return control.valid ? 'valid' : null;
  }

  onSendVerificationCode(): void {
    this.sendVerificationCode.emit();
    this.startCountdown();
  }

  startCountdown(): void {
    this.countdown$ = timer(0, 1000).pipe(
      map(i => 60 - i),
      takeWhile(value => value >= 0)
    );
  }

  writeValue(value: string): void {
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
}
